/*
  Team Development · Training progress panel
  ----------------------------------------------------------------
  Mounted inside StaffProfile as a tab. Shows the mentoring hub's
  view of a team member's training journey: every module they've
  engaged with, current status, due dates, overdue flags, signed
  competencies with expiry, and whether a goal/action/meeting
  linked a module in.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BookOpenCheck, CheckCircle2, Clock, AlertTriangle, ShieldCheck,
  CalendarDays, Loader2, Link as LinkIcon,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const STATUS_STYLES = {
  assigned: "bg-indigo-100 text-indigo-800 border-indigo-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const STATUS_LABELS = {
  assigned: "Assigned",
  in_progress: "In progress",
  completed: "Completed",
};

function StatCard({ label, value, tone = "zinc", icon: Icon }) {
  const toneClasses = {
    zinc: "bg-white border-zinc-200 text-zinc-900",
    indigo: "bg-indigo-50/70 border-indigo-200 text-indigo-900",
    amber: "bg-amber-50/70 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50/70 border-emerald-200 text-emerald-900",
    red: "bg-red-50/70 border-red-200 text-red-900",
  }[tone];
  return (
    <div className={`border rounded p-2 ${toneClasses}`}>
      <div className="flex items-center gap-1 text-2xs uppercase tracking-wider opacity-75">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}

export default function TrainingProgress({ staffId, me, canSign }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/team-development/training-progress?staff_id=${staffId}`);
      setData(r.data);
    } catch (e) {
      toast.error("Couldn't load training progress", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, [staffId]);
  useEffect(() => { load(); }, [load]);

  const modules = data?.modules || [];
  const counts = data?.counts || { assigned: 0, in_progress: 0, completed: 0, overdue: 0, expiring_soon: 0, expired: 0 };

  const filtered = useMemo(() => {
    if (filter === "all") return modules;
    if (filter === "open") return modules.filter((m) => m.assignment && m.assignment.status !== "completed");
    if (filter === "overdue") return modules.filter((m) => m.assignment?.overdue);
    if (filter === "completed") return modules.filter((m) => m.assignment?.status === "completed");
    if (filter === "linked") return modules.filter((m) => (m.development_links || []).length > 0);
    if (filter === "expiring") return modules.filter((m) => m.competency && (m.competency.expiry_state === "expiring_soon" || m.competency.expiry_state === "expired"));
    return modules;
  }, [modules, filter]);

  if (loading) {
    return (
      <div className="text-xs text-zinc-500 flex items-center gap-2 py-6">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading training progress…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="td-training-progress">
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
        <StatCard label="Assigned" value={counts.assigned} tone="indigo" icon={BookOpenCheck} />
        <StatCard label="In progress" value={counts.in_progress} tone="amber" icon={Clock} />
        <StatCard label="Completed" value={counts.completed} tone="emerald" icon={CheckCircle2} />
        <StatCard label="Overdue" value={counts.overdue} tone={counts.overdue > 0 ? "red" : "zinc"} icon={AlertTriangle} />
        <StatCard label="Expiring" value={counts.expiring_soon} tone={counts.expiring_soon > 0 ? "amber" : "zinc"} icon={CalendarDays} />
        <StatCard label="Expired" value={counts.expired} tone={counts.expired > 0 ? "red" : "zinc"} icon={ShieldCheck} />
      </div>

      <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5" data-testid="td-training-filter">
        {[
          { k: "all", label: "All" },
          { k: "open", label: "Open" },
          { k: "overdue", label: "Overdue" },
          { k: "completed", label: "Completed" },
          { k: "expiring", label: "Expiring" },
          { k: "linked", label: "Linked to development" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            className={`text-2xs px-2.5 py-1 rounded ${filter === t.k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
            data-testid={`td-training-filter-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <Card className="p-4 text-center text-xs text-zinc-500">
          {modules.length === 0
            ? "No training modules on record for this team member yet."
            : "No modules match this filter."}
        </Card>
      )}

      <div className="space-y-2">
        {filtered.map((m) => {
          const a = m.assignment;
          const c = m.competency;
          const links = m.development_links || [];
          return (
            <Card
              key={m.module_id}
              className="p-3 rounded-md border border-zinc-200 shadow-none"
              data-testid={`td-training-module-${m.module_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-zinc-950">
                      {m.module_title || "(untitled module)"}
                    </div>
                    {m.module_category && (
                      <Badge className="bg-zinc-100 text-zinc-700 border-zinc-200 text-2xs uppercase tracking-wider">
                        {m.module_category}
                      </Badge>
                    )}
                    {m.module_archived && (
                      <Badge className="bg-zinc-100 text-zinc-500 border-zinc-200 text-2xs">Archived</Badge>
                    )}
                    {a && (
                      <Badge className={`text-2xs ${STATUS_STYLES[a.status] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                        {STATUS_LABELS[a.status] || a.status}
                      </Badge>
                    )}
                    {a?.overdue && (
                      <Badge className="bg-red-100 text-red-800 border-red-200 text-2xs">
                        <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Overdue
                      </Badge>
                    )}
                  </div>

                  <div className="text-2xs text-zinc-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {a?.assigned_at && <span>Assigned {fmtDate(a.assigned_at)}</span>}
                    {a?.self_assigned && <span className="italic">self-started</span>}
                    {a?.due_at && (
                      <span className={a.overdue ? "text-red-700 font-medium" : ""}>
                        Due {fmtDate(a.due_at)}
                      </span>
                    )}
                    {a?.completed_at && <span className="text-emerald-700">Completed {fmtDate(a.completed_at)}</span>}
                    {!a && <span className="italic">No active assignment</span>}
                  </div>

                  {c && (
                    <div className="mt-2 bg-emerald-50/50 border border-emerald-100 rounded px-2 py-1 text-2xs">
                      <div className="flex items-center gap-1 text-emerald-900 font-medium">
                        <ShieldCheck className="h-3 w-3" />
                        Competency signed by {c.signed_by_name || "manager"} on {fmtDate(c.signed_at)}
                        {c.expiry_date && (
                          <span className="font-normal text-zinc-600">
                            · renews {fmtDate(c.expiry_date)}
                          </span>
                        )}
                        {c.expiry_state === "expiring_soon" && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-2xs ml-1">
                            Expiring soon
                          </Badge>
                        )}
                        {c.expiry_state === "expired" && (
                          <Badge className="bg-red-100 text-red-800 border-red-200 text-2xs ml-1">
                            Expired
                          </Badge>
                        )}
                      </div>
                      {c.note && <div className="text-2xs text-zinc-700 mt-0.5">{c.note}</div>}
                    </div>
                  )}

                  {links.length > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-2xs text-indigo-700">
                      <LinkIcon className="h-3 w-3" />
                      Linked to {links.length} development item{links.length === 1 ? "" : "s"}
                      {links.some((l) => l.goal_id) && " · goal"}
                      {links.some((l) => l.action_id) && " · action"}
                      {links.some((l) => l.meeting_id) && " · meeting"}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <Button
                    size="sm" variant="outline" className="text-2xs h-6"
                    onClick={() => window.open(`/learning/${m.module_id}`, "_blank")}
                    data-testid={`td-training-open-${m.module_id}`}
                  >
                    Open module
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
